import { useZoneStore } from '../store/zoneStore';
import { useTabStore, type TabType } from '../store/tabStore';
import { useNetworkElementsStore } from '../store/networkElementsStore';
import { useSettingsStore } from '../store/settingsStore';
import { useLoadFlowStore, initLoadFlowSubscription } from '../store';
import { useNetzanschlussStore } from '../store/netzanschlussStore';
import { networkUpdatesService } from './networkUpdatesService';

export interface SwitchZoneOptions {
  /** Tab to activate after the zone is selected (before the data loads). */
  tab?: Extract<TabType, 'map' | 'monitoring'>;
  /**
   * Give the map container a frame to become visible before the elements arrive —
   * MapViewHandler can only fit the bounds of a map that has dimensions. Needed when
   * the switch also opens the map tab from another page.
   */
  waitForMapResize?: boolean;
}

/**
 * The ONE zone switch flow (frontend/CLAUDE.md → "Zone switch"): disconnect the SSE stream of
 * the old zone, clear every zone-scoped store, select the new zone, load its settings and
 * network, reconnect SSE and restart the load-flow polling. Every entry point (dashboard card,
 * sidebar dropdown, neighbour polygon on the map, header zone search) goes through here so no
 * path forgets a step.
 *
 * @returns false when the zone id is unknown (nothing is changed then).
 */
export async function switchZone(zoneId: string, options: SwitchZoneOptions = {}): Promise<boolean> {
  const zoneStore = useZoneStore.getState();
  if (!zoneId || !zoneStore.zones.some(z => z.id === zoneId)) {
    console.warn(`[zoneSwitch] Unknown zone: ${zoneId}`);
    return false;
  }

  // 1. Disconnect SSE from the current zone
  networkUpdatesService.disconnect();

  // 2. Clear the old zone's data
  const elementsStore = useNetworkElementsStore.getState();
  elementsStore.clearElements();
  useLoadFlowStore.getState().clearResult();
  useNetzanschlussStore.getState().clear();

  // 3. Select the new zone (the zone-scoped store subscriptions reset themselves)
  zoneStore.selectZone(zoneId);

  // 4. Switch the tab first so the map container is visible while the data loads
  if (options.tab) {
    useTabStore.getState().openTab(options.tab);
  }

  // 5. Settings can load right away; the network waits for the map container's size
  const settingsLoaded = useSettingsStore.getState().loadSettings();
  if (options.waitForMapResize) {
    await new Promise(resolve => setTimeout(resolve, 150));
  }
  await Promise.all([settingsLoaded, elementsStore.loadElements()]);

  // The user may have switched again while the data was loading — the newer switch owns
  // the SSE connection and the polling (loadElements already discards its stale response).
  if (useZoneStore.getState().activeZoneId !== zoneId) {
    return true;
  }

  // 6. SSE for element updates, REST polling for load flow results
  networkUpdatesService.connect(zoneId);
  initLoadFlowSubscription();
  return true;
}
